import {
  ActionError,
  AttentionBlock,
  Button,
  ChoiceRow,
  Field,
  FieldGrid,
  Radio,
  RadioGroup,
  Select,
  formatPhone,
  titleCase,
  toast,
  useDelayed,
} from '@westhetics/ui'
import { useId, useState, type ReactNode } from 'react'
import { errorMessage, fieldErrors } from '@/lib/errors'
import {
  pendingAnamnesisId,
  providerDown,
  useCancelAnamnesis,
  useIssueAnamnesis,
  usePractitionerOptions,
  useProcedureOptions,
  useResendAnamnesis,
  type AnamnesisChannel,
  type IssueAnamnesisInput,
  type PatientContact,
} from './api'
import { CancelAnamnesisDialog } from './cancel-dialog'
import { CHANNELS, CHANNEL_IN_SENTENCE, CHANNEL_LABELS, copyText } from './labels'
import { LINK_COPIED, LinkPanel } from './link-panel'

/**
 * Emitir e enviar a anamnese (docs/areas/anamnese.md, RN-ANA-01 a 03; fluxo principal, passos 1 e
 * 2). O mesmo formulário serve à aba da ficha e à lista global: procedimento e profissional são
 * opcionais (P-ANA-03) e o canal decide quem entrega o link. O que a API recusa fica na tela com a
 * saída: a paciente que já tem uma não preenchida (reenviar, ou cancelar e enviar outra) e o
 * provedor do canal fora (a anamnese foi criada: copiar o link ou enviar por outro canal).
 */

/** O contato pode não ter chegado: aí todos os canais ficam abertos e a API confere. */
export type IssuePatient = Pick<PatientContact, 'id' | 'name'> &
  Partial<Pick<PatientContact, 'email' | 'whatsapp'>>

interface ReadyLink {
  url: string
  expiresAt?: string
  /** Já abre com o QR à vista (a pessoa pediu "Abrir QR"). */
  qrOpen?: boolean
}

interface Down {
  anamnesisId: string | null
  url: string | null
  message: string
  channel: AnamnesisChannel
}

/** Por que o canal não pode ser escolhido; `null` quando pode (ou quando a tela não sabe). */
function channelBlocker(channel: AnamnesisChannel, patient: IssuePatient) {
  const noWhatsapp = patient.whatsapp === null || patient.whatsapp === ''
  const noEmail = patient.email === null || patient.email === ''
  if (channel === 'whatsapp' && noWhatsapp) return 'Sem WhatsApp no cadastro.'
  if (channel === 'email' && noEmail) return 'Sem e-mail no cadastro.'
  if (channel === 'both' && noWhatsapp && noEmail) return 'Sem WhatsApp nem e-mail no cadastro.'
  if (channel === 'both' && noWhatsapp) return 'Falta o WhatsApp no cadastro.'
  if (channel === 'both' && noEmail) return 'Falta o e-mail no cadastro.'
  return null
}

function channelDescription(channel: AnamnesisChannel, patient: IssuePatient) {
  if (channel === 'whatsapp') return patient.whatsapp ? formatPhone(patient.whatsapp) : undefined
  if (channel === 'email') return patient.email ?? undefined
  if (channel === 'both') return 'A mesma mensagem pelos dois, com o mesmo link.'
  return 'Você copia o link ou mostra o QR; o sistema não envia nada.'
}

const WAIT_SENTENCES: Record<AnamnesisChannel, string> = {
  whatsapp: 'Enviando por WhatsApp. Em instantes ela recebe o link.',
  email: 'Enviando por e-mail. Em instantes ela recebe o link.',
  both: 'Enviando por WhatsApp e e-mail. Em instantes ela recebe o link.',
  link: 'Gerando o link da anamnese.',
}

export function IssueAnamnesis({
  patient,
  primary = false,
  onClose,
  aside,
}: {
  patient: IssuePatient
  /** O envio é a ação primária da tela (lista global); na ficha, a primária é do cabeçalho. */
  primary?: boolean
  /** Fecha o painel: depois de enviar, ou quando a pessoa desiste. */
  onClose: () => void
  /** Ao lado de "Desistir": "Trocar paciente", na lista global. */
  aside?: ReactNode
}) {
  const name = titleCase(patient.name)
  const channelLabelId = useId()
  const issue = useIssueAnamnesis()
  const resend = useResendAnamnesis()
  const cancel = useCancelAnamnesis()
  const procedures = useProcedureOptions()
  const practitioners = usePractitionerOptions()

  const firstOpen = CHANNELS.find((channel) => !channelBlocker(channel, patient)) ?? 'link'
  const [channel, setChannel] = useState<AnamnesisChannel>(firstOpen)
  const [procedureId, setProcedureId] = useState('')
  const [practitionerId, setPractitionerId] = useState('')
  const [refused, setRefused] = useState<Record<string, string>>({})
  const [failure, setFailure] = useState<{ title: string; description: string } | null>(null)
  const [pendingId, setPendingId] = useState<{ id: string; message: string } | null>(null)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [down, setDown] = useState<Down | null>(null)
  const [readyLink, setReadyLink] = useState<ReadyLink | null>(null)

  const showWait = useDelayed(issue.isPending)

  const input: IssueAnamnesisInput = {
    patientId: patient.id,
    channel,
    ...(procedureId ? { procedureId } : {}),
    ...(practitionerId ? { practitionerId } : {}),
  }

  function clearOutcome() {
    setFailure(null)
    setPendingId(null)
    setRefused({})
  }

  function send() {
    clearOutcome()
    issue.mutate(input, {
      onSuccess: (result) => {
        const url = result.link.url
        if (channel === 'link' && url) {
          // No canal `link` o sistema não envia nada: o endereço fica na tela, com o QR.
          setReadyLink({ url, expiresAt: result.link.expiresAt })
          void copyText(url).then((copied) => {
            if (copied) toast(result.message || LINK_COPIED)
          })
          return
        }
        toast(result.message)
        onClose()
      },
      onError: (error) => {
        const unavailable = providerDown(error)
        if (unavailable) {
          setDown({ ...unavailable, message: errorMessage(error), channel })
          return
        }
        const existing = pendingAnamnesisId(error)
        if (existing) {
          setPendingId({ id: existing, message: errorMessage(error) })
          return
        }
        const fields = fieldErrors(error)
        const known = ['channel', 'procedureId', 'practitionerId', 'patientId'].filter(
          (field) => fields[field],
        )
        if (known.length > 0) {
          setRefused(fields)
          return
        }
        setFailure({
          title: `Não foi possível enviar a anamnese de ${name}`,
          description: errorMessage(error),
        })
      },
    })
  }

  /** Reenvia a anamnese que já existe, pelo canal escolhido aqui (ou por outro, se o provedor caiu). */
  function resendExisting(id: string, by: AnamnesisChannel) {
    setFailure(null)
    resend.mutate(
      { id, channel: by },
      {
        onSuccess: (result) => {
          const url = result.link.url
          if (by === 'link' && url) {
            setPendingId(null)
            setDown(null)
            setReadyLink({ url, expiresAt: result.link.expiresAt })
            void copyText(url).then((copied) => {
              if (copied) toast(LINK_COPIED)
            })
            return
          }
          toast(result.message)
          onClose()
        },
        onError: (error) =>
          setFailure({
            title: `Não foi possível reenviar a anamnese de ${name}`,
            description: fieldErrors(error).channel ?? errorMessage(error),
          }),
      },
    )
  }

  /** "Cancelar e enviar outra": cancela a que barrava e emite a nova, com o que está no formulário. */
  function cancelAndSend(id: string) {
    cancel.mutate(
      { id },
      {
        onSuccess: () => {
          setConfirmingCancel(false)
          // A emissão recusada e a nova têm o mesmo corpo, mas são intenções diferentes.
          issue.renewKey()
          send()
        },
        onError: (error) => {
          setConfirmingCancel(false)
          setFailure({
            title: `Não foi possível cancelar a anamnese anterior de ${name}`,
            description: errorMessage(error),
          })
        },
      },
    )
  }

  if (readyLink) {
    return (
      <AttentionBlock
        variant="lilac"
        role="note"
        title={`Link da anamnese de ${name}`}
        aria-label="Link da anamnese"
      >
        <p>
          O sistema não enviou nada: entregue o link à paciente, ou mostre o QR para ela abrir no
          celular. São 5 blocos curtos, cerca de 4 minutos.
        </p>
        <LinkPanel
          className="mt-3"
          url={readyLink.url}
          patientName={patient.name}
          expiresAt={readyLink.expiresAt}
          defaultQrOpen={readyLink.qrOpen}
        />
        <Button variant="text" className="mt-4" onClick={onClose}>
          Fechar
        </Button>
      </AttentionBlock>
    )
  }

  if (down) {
    const other: AnamnesisChannel = down.channel === 'email' ? 'whatsapp' : 'email'
    const canUseOther = !channelBlocker(other, patient)
    const url = down.url
    return (
      <div className="flex flex-col gap-4">
        <ActionError
          title={`A anamnese de ${name} foi criada, mas a mensagem não saiu`}
          description={`${down.message} O link já existe e vale por 7 dias.`}
          actions={
            <>
              {url ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    void copyText(url).then((copied) => {
                      if (copied) toast(LINK_COPIED)
                      else setReadyLink({ url })
                    })
                  }
                >
                  Copiar link
                </Button>
              ) : null}
              {down.anamnesisId && canUseOther ? (
                <Button
                  variant="outline"
                  size="sm"
                  loading={resend.isPending}
                  loadingLabel="Enviando…"
                  onClick={() => resendExisting(down.anamnesisId!, other)}
                >
                  Enviar por {CHANNEL_IN_SENTENCE[other]}
                </Button>
              ) : null}
              {url ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReadyLink({ url, qrOpen: true })}
                >
                  Abrir QR
                </Button>
              ) : null}
            </>
          }
          onDismiss={onClose}
        />
        {failure ? (
          <ActionError
            title={failure.title}
            description={failure.description}
            onDismiss={() => setFailure(null)}
          />
        ) : null}
      </div>
    )
  }

  // Sem `<form>`: a aba vive dentro da ficha, que pode ter o formulário dela em volta.
  return (
    <div role="group" aria-label="Enviar anamnese" className="flex flex-col gap-4">
      {pendingId ? (
        <ActionError
          title={`${name} já tem uma anamnese não preenchida`}
          description={pendingId.message}
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                loading={resend.isPending}
                loadingLabel="Reenviando…"
                onClick={() => resendExisting(pendingId.id, channel)}
              >
                Reenviar
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={resend.isPending}
                onClick={() => setConfirmingCancel(true)}
              >
                Cancelar e enviar outra
              </Button>
            </>
          }
          onDismiss={() => setPendingId(null)}
        />
      ) : null}
      {failure ? (
        <ActionError
          title={failure.title}
          description={failure.description}
          onDismiss={() => setFailure(null)}
        />
      ) : null}

      <FieldGrid>
        <Field
          label="Procedimento"
          optional
          error={refused.procedureId}
          hint="Aparece na mensagem e na leitura como o procedimento na época."
        >
          <Select
            value={procedureId}
            placeholder={procedures.isPending ? 'Carregando os procedimentos' : 'Sem procedimento'}
            onChange={(event) => {
              setProcedureId(event.target.value)
              setRefused(({ procedureId: _refused, ...rest }) => rest)
            }}
          >
            {(procedures.data ?? []).map((procedure) => (
              <option key={procedure.id} value={procedure.id}>
                {procedure.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label="Profissional"
          optional
          error={refused.practitionerId}
          hint="Sem profissional, só a gestora lê as respostas até um atendimento ligar a anamnese a alguém."
        >
          <Select
            value={practitionerId}
            placeholder={
              practitioners.isPending ? 'Carregando as profissionais' : 'Sem profissional'
            }
            onChange={(event) => {
              setPractitionerId(event.target.value)
              setRefused(({ practitionerId: _refused, ...rest }) => rest)
            }}
          >
            {(practitioners.data ?? []).map((practitioner) => (
              <option key={practitioner.id} value={practitioner.id}>
                {titleCase(practitioner.name)}
              </option>
            ))}
          </Select>
        </Field>
      </FieldGrid>

      <div>
        <div className="eyebrow" id={channelLabelId}>
          Enviar por
        </div>
        <RadioGroup
          aria-labelledby={channelLabelId}
          className="tablet:grid-cols-2 mt-[6px]"
          value={channel}
          onValueChange={(value) => {
            setChannel(value as AnamnesisChannel)
            setRefused(({ channel: _refused, ...rest }) => rest)
          }}
        >
          {CHANNELS.map((option) => {
            const blocker = channelBlocker(option, patient)
            return (
              <ChoiceRow
                key={option}
                className={blocker ? 'cursor-default' : undefined}
                control={
                  <Radio
                    value={option}
                    disabled={Boolean(blocker)}
                    aria-label={
                      blocker ? `${CHANNEL_LABELS[option]} · ${blocker}` : CHANNEL_LABELS[option]
                    }
                  />
                }
                description={blocker ?? channelDescription(option, patient)}
              >
                {CHANNEL_LABELS[option]}
              </ChoiceRow>
            )
          })}
        </RadioGroup>
        {refused.channel || refused.patientId ? (
          <p
            role="alert"
            className="font-body text-eyebrow text-plum mt-[6px] leading-[1.5] font-light tracking-normal"
          >
            {refused.channel ?? refused.patientId}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <Button
          variant={primary ? 'primary' : 'outline'}
          loading={issue.isPending}
          loadingLabel={channel === 'link' ? 'Gerando o link…' : 'Enviando…'}
          disabled={resend.isPending || cancel.isPending}
          onClick={send}
        >
          {channel === 'link' ? 'Gerar link da anamnese' : 'Enviar anamnese'}
        </Button>
        <Button variant="text" onClick={onClose}>
          Desistir do envio
        </Button>
        {aside}
        {showWait ? (
          <span
            role="status"
            aria-live="polite"
            className="font-body text-secondary text-[12px] leading-[1.6] font-light"
          >
            {WAIT_SENTENCES[channel]}
          </span>
        ) : null}
      </div>

      <CancelAnamnesisDialog
        target={
          confirmingCancel && pendingId
            ? { id: pendingId.id, patientName: patient.name, draft: undefined }
            : null
        }
        confirmLabel="Cancelar e enviar outra"
        loading={cancel.isPending || issue.isPending}
        onClose={() => setConfirmingCancel(false)}
        onConfirm={(target) => cancelAndSend(target.id)}
        onResend={(target) => {
          setConfirmingCancel(false)
          resendExisting(target.id, channel)
        }}
      />
    </div>
  )
}
